import React from 'react'
import type {
  FacilitatorLiveSessionCard,
  FacilitatorLiveSessionKind
} from '../../../../../../pure-js/types/FacilitatorLiveSessionMonitor'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { TESTIDS } from '../../../../config/testids'
import { ControlTowerConfirmDialog } from './ControlTowerConfirmDialog'
import type { TranslateFunction } from '../../../shared/helpers/liveActivityHelpers'

interface ControlTowerEndActivityDialogProps {
  card: FacilitatorLiveSessionCard | null
  isEnding: boolean
  onCancel: () => void
  onConfirm: () => void
}

// What ending it does to the people in it, which differs between a game and a debrief.
const describeConsequence = (kind: FacilitatorLiveSessionKind, t: TranslateFunction): string => {
  const copyByKind: Record<FacilitatorLiveSessionKind, string> = {
    game: t(
      'facilitator.controlTower.endActivityForTeamGame',
      'Their game session is closed and every player is taken out of the game. The team is moved on to its next activity.'
    ),
    ai_debrief: t(
      'facilitator.controlTower.endActivityForTeamDebrief',
      'The debrief ends for this team and everyone in it is taken back to their dashboard, where the next activity is waiting.'
    )
  }
  return copyByKind[kind]
}

/** Are-you-sure step before an activity is ended for one team from its card. */
export const ControlTowerEndActivityDialog: React.FC<ControlTowerEndActivityDialogProps> = ({
  card,
  isEnding,
  onCancel,
  onConfirm
}) => {
  const { t } = useTranslation()

  const title = card
    ? t('facilitator.controlTower.endActivityForTeamTitle', 'End {{activity}} for {{team}}?', {
        activity: card.activityTitle,
        team: card.teamName
      })
    : ''

  const body = card ? describeConsequence(card.kind, t) : ''

  const confirmLabel = isEnding
    ? t('facilitator.controlTower.endActivityForTeamEnding', 'Ending…')
    : t('facilitator.controlTower.endActivityForTeamConfirm', 'End for this team')

  return (
    <ControlTowerConfirmDialog
      isOpen={Boolean(card)}
      title={title}
      body={body}
      cancelLabel={t('common.cancel', 'Cancel')}
      confirmLabel={confirmLabel}
      isConfirming={isEnding}
      confirmTestId={TESTIDS.CONTROL_TOWER_END_FOR_TEAM_CONFIRM_BTN}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  )
}
